import React, { useRef, useEffect } from 'react';
import { ImageCarousel } from '../VehicleSlide/ImageCarousel/ImageCarousel';
import { DetailSection, DetailItem } from './DetailSection';
import { VehicleDetailsTitle } from './VehicleDetailsTitle';
import { LoadingSpinner } from './LoadingSpinner';
import { ErrorMessage } from './ErrorMessage';
import {VehicleDetails, FullVehicleDetails} from '../../../vehicleDetailsSlice';

interface VehicleDetailsViewProps {
    vehicle: VehicleDetails;
    onClose: () => void;
    setModalRef?: (ref: HTMLDivElement | null) => void;
    isOpen: boolean;
    isLoading?: boolean;
    error?: string | null;
    fullVehicleDetails?: FullVehicleDetails;
}

export const VehicleDetailsView: React.FC<VehicleDetailsViewProps> = ({
                                                                          vehicle,
                                                                          onClose,
                                                                          setModalRef,
                                                                          isOpen,
                                                                          isLoading,
                                                                          error,
                                                                          fullVehicleDetails
                                                                      }) => {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (modalRef.current) {
            modalRef.current.style.transform = 'translateY(100%)';
            setTimeout(() => {
                if (modalRef.current) {
                    modalRef.current.style.transform = 'translateY(0)';
                }
            }, 10);
        }
    }, []);

    useEffect(() => {
        if (setModalRef && modalRef.current) {
            setModalRef(modalRef.current);
        }

        return () => {
            if (setModalRef) {
                setModalRef(null);
            }
        };
    }, [setModalRef]);

    const renderVehicleDetails = () => {
        if (isLoading) {
            return <LoadingSpinner />;
        }

        if (error) {
            return <ErrorMessage />;
        }

        return (
            <>
                <div className="flex rounded-2xl overflow-hidden">
                    <ImageCarousel
                        images={vehicle.images_url ?? [vehicle.image_url!]}
                        onImageClick={() => {}}
                        height={240}
                        objectFit="cover"
                        className="rounded-2xl overflow-hidden"
                    />
                </div>

                <div className="py-4">
                    <VehicleDetailsTitle vehicle={vehicle}/>
                </div>

                {fullVehicleDetails && Object.entries(fullVehicleDetails.sections || {}).map(([sectionName, fields]) => (
                    <DetailSection key={sectionName} title={sectionName}>
                        {fields.map((field) => (
                            <DetailItem key={field.name} label={field.name} value={field.value} />
                        ))}
                    </DetailSection>
                ))}
            </>
        );
    };

    return (
        <>
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-40 z-50 rounded-t-2xl"
                ></div>
            )}

            <div
                ref={modalRef}
                className="fixed inset-x-0 bottom-0 z-50 h-[88vh] flex flex-col bg-white rounded-t-2xl overflow-hidden shadow-xl transition-transform duration-300 ease-out"
            >
                <div className="flex items-end justify-end p-3 border-b border-gray-200 bg-white">
                    <button
                        onClick={onClose}
                        className="rounded-full hover:bg-gray-100"
                        aria-label="Close"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-4">
                    {renderVehicleDetails()}
                </div>
            </div>
        </>
    );
};
